/**
 * Living Floor Materials - Material presets and shaders
 * Shared material configurations for the living floor system
 */

import * as THREE from 'three';

// Floor material configuration
export const createFloorMaterial = (): THREE.MeshStandardMaterial => {
  return new THREE.MeshStandardMaterial({
    color: '#0a1235', // Slightly lighter so colored light is visible
    emissive: '#1a0060',
    emissiveIntensity: 0.35,
    metalness: 0.15, // Low metalness → diffuse responds to colored lights
    roughness: 0.75, // High roughness → color spreads broadly, not just specular
  });
};

// Particle material configuration - supports vertex colors for bioluminescent gradients
export const createParticleMaterial = (config?: {
  size?: number;
  opacity?: number;
  vertexColors?: boolean;
}): THREE.PointsMaterial => {
  return new THREE.PointsMaterial({
    color: config?.vertexColors ? 0xffffff : 0x3ba4ff,
    size: config?.size ?? 0.3,
    sizeAttenuation: true,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    opacity: config?.opacity ?? 1.0,
    vertexColors: config?.vertexColors ?? false,
    blending: THREE.AdditiveBlending,
  });
};

// Panel shadow material - semi-transparent dark overlay
export const createPanelShadowMaterial = (): THREE.MeshBasicMaterial => {
  return new THREE.MeshBasicMaterial({
    color: '#000000',
    transparent: true,
    opacity: 0.2,
  });
};

// Cursor trail material - bright glow effect
export const createCursorTrailMaterial = (): THREE.PointsMaterial => {
  return new THREE.PointsMaterial({
    color: '#ff6b35',
    size: 0.15,
    sizeAttenuation: true,
    transparent: true,
    depthWrite: false,
    opacity: 1.0,
  });
};

// Aurora sweep material - gaussian falloff chromatic emission planes
// Creates elongated bioluminescent fields like deep-ocean aurora
export const createAuroraMaterial = (params: {
  colorA: THREE.Color;
  colorB: THREE.Color;
  offsetY?: number;
  scaleX?: number;
  scaleY?: number;
  intensity?: number;
}): THREE.ShaderMaterial => {
  return new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      colorA: { value: params.colorA },
      colorB: { value: params.colorB },
      offsetY: { value: params.offsetY ?? 0.0 },
      scaleX: { value: params.scaleX ?? 0.45 },
      scaleY: { value: params.scaleY ?? 0.18 },
      intensity: { value: params.intensity ?? 1.0 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float time;
      uniform vec3 colorA;
      uniform vec3 colorB;
      uniform float offsetY;
      uniform float scaleX;
      uniform float scaleY;
      uniform float intensity;
      varying vec2 vUv;

      void main() {
        // Centered UV with vertical offset
        vec2 uv = vUv - vec2(0.5, 0.5 - offsetY);

        // Animated vertical drift — sinusoidal ripple across the width
        float drift = sin(time * 0.25 + vUv.x * 6.28) * 0.025
                    + sin(time * 0.17 + vUv.x * 3.14 + 1.2) * 0.015;
        float yd = uv.y + drift;

        // Elongated gaussian: wide horizontal, narrow vertical
        float gauss = exp(-(uv.x * uv.x) / (2.0 * scaleX * scaleX)
                         -(yd   * yd)   / (2.0 * scaleY * scaleY));

        // Spectral interference — second lobe offset slightly in Y
        float drift2 = sin(time * 0.19 + vUv.x * 4.71 + 2.5) * 0.02;
        float yd2 = uv.y + drift2 + 0.04;
        float gauss2 = exp(-(uv.x * uv.x) / (2.0 * scaleX * scaleX)
                          -(yd2  * yd2)  / (2.0 * (scaleY * 0.6) * (scaleY * 0.6)));

        float alpha = clamp(gauss * 0.75 + gauss2 * 0.35, 0.0, 1.0) * intensity;

        // Color: blend from colorA (left) to colorB (right), lobe2 shifts toward colorB
        vec3 col = mix(colorA, colorB, vUv.x);
        col = mix(col, colorB, gauss2 * 0.4);

        gl_FragColor = vec4(col, alpha);
      }
    `,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
};

// Horizon glow — thin luminous line at the far edge of the perspective floor
export const createHorizonGlowMaterial = (): THREE.MeshBasicMaterial => {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(0.45, 0.95, 1.0),
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
};

// Floor vertex shader - procedural displacement
export const floorVertexShader = `
  uniform float time;
  uniform float waveAmplitude;
  uniform float waveFrequency;

  varying float vHeight;
  varying vec3 vPosition;

  void main() {
    vPosition = position;

    // Breathing effect - subtle height variation
    float wave = sin(position.x * waveFrequency + time) *
                 cos(position.z * waveFrequency + time) *
                 waveAmplitude;

    vec3 pos = position;
    pos.y += wave;

    vHeight = wave;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

// Floor fragment shader - emissive gradient
export const floorFragmentShader = `
  uniform vec3 baseColor;
  uniform vec3 emissiveColor;
  uniform float emissiveIntensity;

  varying float vHeight;
  varying vec3 vPosition;

  void main() {
    // Add height-based color variation
    vec3 color = baseColor + emissiveColor * (vHeight + 0.5) * emissiveIntensity;

    // Slight fresnel effect for depth
    float fresnel = pow(1.0 - abs(dot(normalize(vPosition), vec3(0.0, 1.0, 0.0))), 2.0);
    color += emissiveColor * fresnel * 0.1;

    gl_FragColor = vec4(color, 1.0);
  }
`;

// Particle system compute shader (simulated in CPU, but here's the concept)
export const particleComputeShader = `
  // This runs as a GPU compute shader (simplified for compatibility)
  uniform sampler2D panelForces;
  uniform vec3 cursorPos;
  uniform float time;
  uniform float repulsion;
  uniform float attraction;

  varying vec3 vVelocity;

  void main() {
    // In production, this would run on GPU
    // For now, we compute particle physics in JavaScript
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Color palette for the living floor — bioluminescent void
export const FLOOR_PALETTE = {
  floorBase: '#060818', // Deep void black
  floorEmissive: '#0d0040', // Deep indigo emission
  particleColor: '#3ba4ff', // Cyan fallback
  panelShadow: '#000000', // Black
  cursorTrail: '#ff6b35', // Warm orange
  activeUser: '#2ecc71', // Green
  bloomColor: '#00ff88', // Neon green
  auroraViolet: '#5500ff', // Electric indigo
  auroraCyan: '#00d4f2', // Teal-cyan
  auroraMagenta: '#cc00ff', // Deep magenta
} as const;

// Material factory for creating themed materials
export const MaterialFactory = {
  floor: createFloorMaterial,
  particles: createParticleMaterial,
  panelShadow: createPanelShadowMaterial,
  cursorTrail: createCursorTrailMaterial,
  aurora: createAuroraMaterial,
  horizonGlow: createHorizonGlowMaterial,
};
